/**
 * Josephine & June · June Club membership cart logic (jj-vip).
 *
 * Loaded as a module by snippets/jj-vip-config.liquid (the one jj line in layout/theme.liquid),
 * which also holds its config <script id="jj-vip-config"> (Theme settings > June Club), the inlined
 * jj-vip.css and a small classic guard script that keeps a locked checkout locked when this module
 * fails to load (see qa/cart-membership.md §2.4).
 *
 * What it does:
 * 1. After every cart change (Horizon 4.1 event `shopify:cart:lines-update`), on page load when
 *    Liquid saw a vault piece, the membership, a consent attribute or the tab holds a tick, on
 *    back/forward cache restores and when the tab becomes visible again with a non-empty cart, it
 *    fetches /cart.js and marks every line that is a vault piece: SKU starting with `VAULT-`
 *    (data/product-conventions.md §2), safety net price $0 and 1000 g. Cart AJAX lines carry
 *    no tags, so no product lookups.
 *    - vault piece in the cart, no membership line: adds the membership variant once
 *      (quantity 1, hidden line property `_jj_auto: 1`) and morphs the drawer and cart page
 *      straight from the add.js Section Rendering HTML through Horizon's own CartLinesUpdateEvent.
 *    - no vault piece left and every membership line carries `_jj_auto`: removes it. A
 *      membership added on purpose (join card, no `_jj_auto`) stays.
 *    - more than one membership line or quantity above 1: back to one line, quantity 1.
 *    While one of these writes runs, every cart-items-component gets Horizon's own
 *    `cart-items-disabled` class, so Horizon's line-index updates cannot hit a shifted line.
 *    When the drawer or cart page shows another cart than the server holds (another tab, a
 *    failed request), the cart sections are rendered again.
 * 2. When the cart holds the membership or a vault piece, snippets/jj-cart-consent.liquid
 *    renders the unticked consent checkbox next to the checkout button. This script fills the
 *    first charge date (today + trial days, US format), restores a tick from sessionStorage
 *    for the same cart token only, writes the cart attributes "June Club consent" (ISO time of
 *    the tick) and "June Club terms" (the exact text that was shown) via /cart/update.js, and
 *    keeps every checkout button disabled + aria-disabled until the box is ticked and the
 *    membership is in the cart. A MutationObserver on the cart-items-component roots re-applies
 *    that state after each morph (batched per frame).
 * 3. Checkout guard (capture-phase click and submit on window): a click only goes through
 *    untouched when the consent is saved on the cart and the cart was read from the server less
 *    than a few seconds ago. Otherwise it is held, /cart.js is read, the page is re-rendered if
 *    it is stale, the consent attributes are rewritten if they differ from the text shown now,
 *    and only then the same button is clicked again (Reign's redirect takes over from there).
 * 4. Header cart bubble (rip plan F7): Horizon counts every line, so the trial line made one Vault
 *    piece read "2". After each cart change and server read the bubble shows the shopper's own pieces.
 *
 * Public hooks for other jj code: window.JJVip (config, refresh(), state) and the document
 * event `jj:vip:change` ({hasVault, hasMembership, consented, locked}).
 * With an empty membership_variant_id the script logs one warning and does nothing; vault-tagged
 * pieces then get an "unavailable" block from Liquid and the checkout stays locked (CSS + classic guard).
 */

const LOG = '[jj-vip]';
const CONFIG_ID = 'jj-vip-config';
const STORE_CONSENT = 'jj-vip:consent';
const MAX_SAME_CART_ADDS = 3; // adds in a row for one unchanged set of shopper lines (something keeps removing the line)
const LOOP_BREAKER_MAX = 20; // last-resort cap on auto-adds per LOOP_BREAKER_WINDOW_MS
const LOOP_BREAKER_WINDOW_MS = 120000;
const ADD_RETRY_DELAYS_MS = [1000, 3000]; // wait before the 2nd and the 3rd attempt
const MAX_PASSES = 6;
const FRESH_MS = 3000; // a server read younger than this lets a checkout click through untouched
const HORIZON_IDLE_WAIT_MS = 2000;

const CHECKOUT_SELECTOR = [
  'button[name="checkout"]',
  'input[type="submit"][name="checkout"]',
  '.cart__checkout-button',
  'a[href$="/checkout"]',
  'a[href*="/checkout?"]',
  'a[href*="/checkouts/"]',
  'form[action*="/checkout"] [type="submit"]',
].join(',');

const MESSAGES = {
  tick: 'Check the box above to continue to checkout.',
  membership: 'Adding your June Club trial to your bag. One moment, then try again.',
  membershipFailed: 'We couldn\'t add your June Club trial. Please refresh the page and try again.',
  saveFailed: 'Something went wrong saving your choice. Please try again.',
};

const cfg = readConfig();
const memberId = cfg ? String(cfg.membershipVariantId ?? '').trim() : '';

if (!cfg) {
  console.warn(LOG, 'config (snippets/jj-vip-config.liquid) not found: June Club cart logic is off.');
} else if (!/^\d+$/.test(memberId)) {
  // snippets/jj-cart-consent.liquid then renders an "unavailable" block next to the checkout for
  // vault-tagged pieces, and the CSS + the classic guard in jj-vip-config.liquid keep it locked.
  console.warn(
    LOG,
    'Theme settings > June Club > membership_variant_id is empty or not a number: June Club cart logic is off and vault pieces cannot check out.'
  );
} else {
  init();
}

/* ------------------------------------------------------------------ setup */

function readConfig() {
  const el = document.getElementById(CONFIG_ID);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || '{}');
  } catch (error) {
    console.warn(LOG, 'config is not valid JSON', error);
    return null;
  }
}

function init() {
  const root = String(cfg.rootUrl || '/').replace(/\/?$/, '/');
  const vaultSkuPrefix = String(cfg.vaultSkuPrefix || 'VAULT-').trim().toUpperCase();
  const vaultGrams = Number(cfg.vaultGrams) || 1000;
  const trialDays = Math.max(0, Number(cfg.trialDays) || 0);
  const consentAttr = cfg.consentAttribute || 'June Club consent';
  const termsAttr = cfg.termsAttribute || 'June Club terms';
  const autoProp = cfg.autoProperty || '_jj_auto';

  const routes = {
    cart: `${root}cart.js`,
    add: `${root}cart/add.js`,
    update: `${root}cart/update.js`,
  };

  const storedConsent = readStore(STORE_CONSENT);

  const state = {
    hasVault: Boolean(cfg.cart?.hasVault),
    hasMembership: Boolean(cfg.cart?.hasMembership),
    /** ISO time of the tick, null when unticked */
    consentAt: typeof storedConsent?.at === 'string' ? storedConsent.at : null,
    /** cart token the tick belongs to (null until the first server read after the tick) */
    consentToken: typeof storedConsent?.token === 'string' ? storedConsent.token : null,
    /** consentAt value the server cart attribute holds, null when it does not match */
    savedAt: null,
    /** "June Club terms" value the server cart attribute holds */
    savedTerms: null,
    cartToken: null,
    /** item count of the last known cart (Liquid on page load, then every cart read) */
    itemCount: Number(cfg.cart?.itemCount) || 0,
    /** Date.now() of the last successful server read */
    serverAt: 0,
    failed: false,
    lastAddSignature: '',
    sameCartAdds: 0,
    loopAdds: /** @type {number[]} */ ([]),
    addFailures: 0,
    resyncedSignature: '',
    running: /** @type {Promise<void>|null} */ (null),
    rerun: false,
    writeChain: Promise.resolve(),
    lineWrites: 0,
    own: false,
    bypass: false,
    proceeding: false,
  };

  /* ---------------------------------------------------------- helpers */

  function readStore(key) {
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      if (value == null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* private mode or storage full: the tick then lives for this page only */
    }
  }

  function storeConsent() {
    writeStore(STORE_CONSENT, state.consentAt ? { at: state.consentAt, token: state.consentToken } : null);
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function warn(...args) {
    console.warn(LOG, ...args);
  }

  /** The part of a cart token before `?key=`, stable for the life of a cart. */
  function tokenId(token) {
    return String(token || '').split('?')[0].trim();
  }

  /* ---------------------------------------------------------- network */

  async function getCart() {
    const response = await fetch(routes.cart, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || (json && json.status && Number(json.status) >= 400)) {
      const error = new Error(json?.description || json?.message || `${url} ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return json;
  }

  function cartComponents() {
    return [...document.querySelectorAll('cart-items-component[data-section-id]')].filter(
      (el) => el instanceof HTMLElement && el.dataset.sectionId
    );
  }

  function sectionIds() {
    return [...new Set(cartComponents().map((el) => el.dataset.sectionId))];
  }

  function withSections(body) {
    const ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  /* ------------------------------------------------------ vault check */

  function isMembershipLine(item) {
    return String(item.variant_id) === memberId;
  }

  function isAutoLine(item) {
    const value = item.properties?.[autoProp];
    return value != null && String(value) !== '';
  }

  /** data/product-conventions.md §2: SKU prefix `VAULT-`, safety net $0 and 1000 g. */
  function isVaultLine(item) {
    if (isMembershipLine(item)) return false;
    const sku = String(item.sku || '').trim().toUpperCase();
    if (vaultSkuPrefix && sku.startsWith(vaultSkuPrefix)) return true;
    const price = Number(item.original_price ?? item.price);
    return price === 0 && Number(item.grams) === vaultGrams;
  }

  function analyse(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const memberLines = items.filter(isMembershipLine);
    const vaultLines = items.filter(isVaultLine);
    return {
      memberLines,
      vaultLines,
      hasMembership: memberLines.length > 0,
      hasVault: vaultLines.length > 0,
    };
  }

  /** The shopper's own lines (everything but the membership), used to tell a changed cart from a loop. */
  function shopperSignature(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items
      .filter((item) => !isMembershipLine(item))
      .map((item) => `${item.key}:${item.quantity}`)
      .join('|');
  }

  function cartSignature(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return `${tokenId(cart?.token)}#${items.map((item) => `${item.key}:${item.quantity}`).join('|')}`;
  }

  /* ------------------------------------------- cart icon count (F7) */

  /**
   * The shopper's own pieces when the June Club trial line sits next to them, else null (nothing to
   * correct: no trial line, or the trial is the only line and the bag is not empty).
   */
  function shopperCount(items) {
    if (!Array.isArray(items)) return null;
    let member = 0;
    let other = 0;
    for (const item of items) {
      const quantity = Number(item?.quantity) || 0;
      if (isMembershipLine(item)) member += quantity;
      else other += quantity;
    }
    return member > 0 && other > 0 ? other : null;
  }

  /**
   * Horizon's header bubble (assets/cart-icon.js) counts every line, so one Vault piece showed "2".
   * Paint the shopper's own count over it and keep Horizon's sessionStorage copy equal, so its
   * pageshow check does not paint the old number back. The drawer badge is server-rendered without
   * the trial line (snippets/jj-cart-badge.liquid).
   */
  function paintCartCount(count) {
    if (count == null) return;
    const text = count < 100 ? String(count) : '';
    for (const el of document.querySelectorAll('cart-icon [ref="cartBubbleCount"]')) {
      if (el.textContent !== text) el.textContent = text;
    }
    try {
      window.sessionStorage.setItem('cart-count', JSON.stringify({ value: text, timestamp: Date.now() }));
    } catch {
      /* storage blocked: the bubble is still painted */
    }
  }

  /**
   * Takes over what the server says: vault/membership flags, cart token, saved consent.
   * `fresh: false` for a cart this script put together itself (the cart before add.js plus the added
   * line): it updates the flags but does not count as a server read for the checkout fast path.
   */
  function adoptServerCart(cart, info, { fresh = true } = {}) {
    state.hasVault = info.hasVault;
    state.hasMembership = info.hasMembership;
    if (fresh) state.serverAt = Date.now();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    state.itemCount = Number(cart?.item_count ?? items.reduce((total, item) => total + (Number(item.quantity) || 0), 0)) || 0;

    const token = tokenId(cart?.token);
    if (token) {
      state.cartToken = token;
      if (state.consentAt && state.consentToken && state.consentToken !== token) {
        // A new cart in the same tab (Reign rotated it, the old one was emptied elsewhere): the
        // tick belonged to the old cart, the new one starts unticked.
        state.consentAt = null;
        state.consentToken = null;
        storeConsent();
      } else if (state.consentAt && !state.consentToken) {
        state.consentToken = token;
        storeConsent();
      }
    }

    const attributes = cart?.attributes || {};
    const savedAt = String(attributes[consentAttr] ?? '');
    if (state.consentAt && savedAt === state.consentAt) {
      state.savedAt = state.consentAt;
      state.savedTerms = String(attributes[termsAttr] ?? '');
    } else {
      state.savedAt = null;
      state.savedTerms = null;
    }

    paintCartCount(shopperCount(items));
  }

  /* --------------------------------------------- cart re-render (Horizon) */

  /** @type {Promise<any>|null} */
  let eventsModule = null;
  function loadEvents() {
    eventsModule ??= import('@shopify/events').catch((error) => {
      warn('@shopify/events could not be loaded, falling back to a direct section render', error);
      return null;
    });
    return eventsModule;
  }

  /** @type {Promise<any>|null} */
  let utilitiesModule = null;
  /**
   * Horizon morphs the first add into an empty drawer inside a view transition. A second cart morph
   * while that transition runs skips it and leaves an unhandled AbortError in the console, so the
   * membership morph waits for it (at most 1 s; no wait when no transition runs).
   */
  async function settleTransition() {
    utilitiesModule ??= import('@theme/utilities').catch(() => null);
    const utilities = await utilitiesModule;
    const current = utilities?.viewTransition?.current;
    if (!current) return;
    await Promise.race([Promise.resolve(current).catch(() => {}), wait(1000)]);
  }

  /** @type {Promise<any>|null} */
  let rendererModule = null;
  function loadRenderer() {
    rendererModule ??= import('@theme/section-renderer').catch((error) => {
      warn('@theme/section-renderer could not be loaded', error);
      return null;
    });
    return rendererModule;
  }

  function cartItemCountFromSections(sections) {
    for (const html of Object.values(sections || {})) {
      const match = /ref="cartItemCount"[^>]*>\s*(\d+)/.exec(String(html));
      if (match) return Number(match[1]);
    }
    return undefined;
  }

  /**
   * Tells Horizon the cart changed, the same way its own components do: a CartLinesUpdateEvent whose
   * promise carries the Section Rendering HTML. cart-items-component morphs the drawer and the cart
   * page straight from that HTML, the cart icon and the live region update their count. Action
   * 'update'/'remove' (never 'add') so the drawer does not pop open on its own. Resolves once the
   * morph has run.
   */
  async function announce(action, lines, cart, sections) {
    await settleTransition();
    const Events = await loadEvents();
    const EventClass = Events?.CartLinesUpdateEvent;
    let eventCart = null;
    if (cart) {
      try {
        eventCart = EventClass?.createCartFromAjaxResponse ? EventClass.createCartFromAjaxResponse(cart) : null;
      } catch {
        eventCart = null;
      }
    }
    const result = {
      cart: eventCart,
      detail: {
        items: cart?.items,
        sections: sections || undefined,
        itemCount: cart?.item_count ?? cartItemCountFromSections(sections),
        source: 'jj-vip',
        didError: false,
      },
    };

    let event = null;
    if (EventClass) {
      try {
        event = new EventClass({ action, context: 'cart', lines, promise: Promise.resolve(result) });
      } catch (error) {
        warn('CartLinesUpdateEvent rejected the payload, falling back to a direct section render', error);
      }
    }

    if (event) {
      state.own = true;
      try {
        document.dispatchEvent(event);
      } finally {
        state.own = false;
      }
      // Horizon morphs in a promise callback of the event; one macrotask later that has run.
      await wait(0);
      return;
    }

    const renderer = await loadRenderer();
    for (const el of cartComponents()) {
      const id = el.dataset.sectionId;
      const mode = el.hasAttribute('data-drawer') ? 'hydration' : 'full';
      const html = sections?.[id];
      try {
        if (html && renderer?.morphSection) await renderer.morphSection(id, html, { mode });
        else await renderer?.sectionRenderer?.renderSection(id, { cache: false, mode });
      } catch (error) {
        warn('section render failed', error);
      }
    }
  }

  async function announceSafely(action, lines, cart, sections) {
    try {
      await announce(action, lines, cart, sections);
    } catch (error) {
      warn('cart re-render failed', error);
    }
  }

  function setCartItemsDisabled(disabled) {
    for (const el of document.querySelectorAll('cart-items-component')) {
      el.classList.toggle('cart-items-disabled', disabled);
    }
  }

  /**
   * Runs a jj cart write with Horizon's own `cart-items-disabled` on every cart-items-component, so
   * no quantity or remove click can go out with a line index that the write is about to shift.
   */
  async function withCartLock(task) {
    state.lineWrites += 1;
    setCartItemsDisabled(true);
    try {
      return await task();
    } finally {
      state.lineWrites -= 1;
      if (state.lineWrites === 0) setCartItemsDisabled(false);
    }
  }

  /** Waits (at most HORIZON_IDLE_WAIT_MS) while Horizon's own cart-items-disabled shows a cart write in flight. */
  async function waitForHorizon() {
    if (state.lineWrites > 0) return;
    const busy = () =>
      [...document.querySelectorAll('cart-items-component')].some((el) => el.classList.contains('cart-items-disabled'));
    const start = Date.now();
    while (busy() && Date.now() - start < HORIZON_IDLE_WAIT_MS) await wait(100);
  }

  /** The cart the page would show right after add.js, without another round trip: new lines first, as Shopify orders them. */
  function mergeAdded(previous, addedItems) {
    const addedKeys = new Set(addedItems.map((item) => item.key));
    const items = [...addedItems, ...(previous?.items || []).filter((item) => !addedKeys.has(item.key))];
    const sum = (field) => addedItems.reduce((total, item) => total + (Number(item[field]) || 0), 0);
    return {
      ...(previous || {}),
      items,
      item_count: items.reduce((total, item) => total + (Number(item.quantity) || 0), 0),
      total_price: (Number(previous?.total_price) || 0) + sum('final_line_price'),
      original_total_price: (Number(previous?.original_total_price) || 0) + sum('original_line_price'),
      items_subtotal_price: (Number(previous?.items_subtotal_price) || 0) + sum('final_line_price'),
    };
  }

  async function addMembership(previousCart) {
    return withCartLock(async () => {
      const added = await postJSON(
        routes.add,
        withSections({ items: [{ id: Number(memberId), quantity: 1, properties: { [autoProp]: '1' } }] })
      );
      state.hasMembership = true;
      const addedItems = Array.isArray(added?.items) ? added.items : [];
      const cart = mergeAdded(previousCart, addedItems);
      const key = addedItems[0]?.key || memberId;
      // Morph from the add.js sections right away: a failing /cart.js afterwards cannot leave the page stale.
      await announceSafely('update', [{ id: String(key), quantity: 1 }], cart, added?.sections);
      return cart;
    });
  }

  /** @param {Record<string, number>} updates line key -> quantity */
  async function setLines(updates) {
    return withCartLock(async () => {
      const cart = await postJSON(routes.update, withSections({ updates }));
      const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
      const removing = lines.every((line) => line.quantity === 0);
      await announceSafely(removing ? 'remove' : 'update', lines, cart, cart.sections);
      return cart;
    });
  }

  /** True when a cart section shows another membership/vault state than the server holds. */
  function pageIsStale(info) {
    const needs = info.hasVault || info.hasMembership;
    return cartComponents().some((component) => {
      const block = component.querySelector('[data-jj-consent]');
      if (!block) return needs;
      if (!needs) return true;
      return (
        block.getAttribute('data-jj-has-membership') !== String(info.hasMembership) ||
        block.getAttribute('data-jj-has-vault') !== String(info.hasVault)
      );
    });
  }

  async function renderCartSections() {
    const renderer = await loadRenderer();
    if (!renderer?.sectionRenderer) return;
    await withCartLock(() =>
      Promise.all(
        cartComponents().map((el) =>
          renderer.sectionRenderer
            .renderSection(el.dataset.sectionId, {
              cache: false,
              mode: el.hasAttribute('data-drawer') ? 'hydration' : 'full',
            })
            .catch((error) => warn('section render failed', error))
        )
      )
    );
  }

  /**
   * Re-renders the cart sections when they disagree with the server. Without `force` only once per
   * cart state: Liquid reads the vault tag, this script the SKU, and a catalog error must not loop.
   * @returns {Promise<boolean>} true when it re-rendered
   */
  async function resyncPage(cart, info, { force = false } = {}) {
    if (!pageIsStale(info)) return false;
    const signature = cartSignature(cart);
    if (!force && state.resyncedSignature === signature) return false;
    state.resyncedSignature = signature;
    await renderCartSections();
    syncDom();
    return true;
  }

  /* ------------------------------------------------------- reconcile */

  function reconcile() {
    if (state.running) {
      state.rerun = true;
      return state.running;
    }
    state.addFailures = 0;
    state.running = (async () => {
      let passes = 0;
      /** The cart right after this run's own write, so the next pass needs no /cart.js read. */
      let hint = null;
      do {
        state.rerun = false;
        passes += 1;
        let result = false;
        try {
          result = await reconcileOnce(hint);
        } catch (error) {
          warn('cart check failed', error);
        }
        // A cart event that arrived during the pass (state.rerun) makes the known cart stale: read it again.
        hint = result && typeof result === 'object' && !state.rerun ? result : null;
        if (result) state.rerun = true;
      } while (state.rerun && passes < MAX_PASSES);
    })().finally(() => {
      state.running = null;
      observeRoots();
      syncDom();
      emitChange();
    });
    return state.running;
  }

  function loopBreakerTripped(now) {
    state.loopAdds = state.loopAdds.filter((time) => now - time < LOOP_BREAKER_WINDOW_MS);
    return state.loopAdds.length >= LOOP_BREAKER_MAX;
  }

  /**
   * @param {{cart: any, fresh: boolean}|null} hint the cart after the previous pass's own write
   * @returns {Promise<boolean|{cart: any, fresh: boolean}>} false: nothing changed. true: the cart
   *   changed, read it again. An object: the cart changed and this is the cart after the change.
   */
  async function reconcileOnce(hint) {
    let cart = hint?.cart;
    if (!cart) {
      await waitForHorizon(); // let a Horizon quantity/remove request land before the cart is read
      cart = await getCart();
    }
    const info = analyse(cart);
    adoptServerCart(cart, info, { fresh: !hint || hint.fresh });
    if (!info.hasVault || info.hasMembership) state.failed = false;
    syncDom();

    // Loop protection counts adds for one unchanged set of shopper lines. A cart without vault pieces
    // or with other shopper lines in between (remove a piece, swap pieces) starts the count again.
    const signature = shopperSignature(cart);
    if (!info.hasVault || signature !== state.lastAddSignature) state.sameCartAdds = 0;

    // 1. Vault piece without membership: add it.
    if (info.hasVault && !info.hasMembership) {
      const now = Date.now();
      if (state.sameCartAdds >= MAX_SAME_CART_ADDS || loopBreakerTripped(now)) {
        state.failed = true;
        showMessage(MESSAGES.membershipFailed);
        warn('the membership line keeps disappearing from the cart, auto-add stopped (an app removing it?)');
        return false;
      }
      try {
        const added = await addMembership(cart);
        state.sameCartAdds += 1;
        state.lastAddSignature = signature;
        state.loopAdds.push(now);
        state.addFailures = 0;
        // The next pass checks the cart add.js described (no extra /cart.js). A line removed again by
        // an app shows up on the next cart event, the next tick (update.js returns the server cart)
        // or the checkout click (always read from the server unless saved moments ago).
        return { cart: added, fresh: false };
      } catch (error) {
        state.addFailures += 1;
        warn(`could not add membership variant ${memberId}`, error);
        if (state.addFailures > ADD_RETRY_DELAYS_MS.length) {
          state.failed = true;
          showMessage(MESSAGES.membershipFailed);
          return false;
        }
        await wait(ADD_RETRY_DELAYS_MS[state.addFailures - 1]);
        return true;
      }
    }

    // 2. Membership lines: remove auto-added ones without vault pieces, otherwise keep exactly one at quantity 1.
    if (info.hasMembership) {
      const updates = {};
      if (!info.hasVault && info.memberLines.every(isAutoLine)) {
        for (const line of info.memberLines) updates[line.key] = 0;
      } else {
        const keep = info.memberLines.find((line) => !isAutoLine(line)) || info.memberLines[0];
        for (const line of info.memberLines) if (line.key !== keep.key) updates[line.key] = 0;
        if (keep.quantity !== 1) updates[keep.key] = 1;
      }
      if (Object.keys(updates).length) {
        const updated = await setLines(updates);
        return updated && Array.isArray(updated.items) ? { cart: updated, fresh: true } : true;
      }
    }

    // 3. Page out of date (another tab, a failed request): render the cart sections again.
    await resyncPage(cart, info);

    // 4. Consent housekeeping.
    const attribute = String(cart.attributes?.[consentAttr] ?? '');
    if (!info.hasVault && !info.hasMembership) {
      if (state.consentAt) setConsent(false, { write: false });
      if (attribute) await queueConsentWrite();
    } else if (state.consentAt && state.savedAt !== state.consentAt) {
      queueConsentWrite().catch((error) => warn('could not save the June Club choice', error));
    }
    return false;
  }

  /* --------------------------------------------------------- consent */

  let chargeDate = { minute: -1, text: '' };
  /** Today + trial days, US format. Formatted at most once a minute. */
  function firstChargeDate() {
    const minute = Math.floor(Date.now() / 60000);
    if (chargeDate.minute !== minute) {
      const date = new Date();
      date.setDate(date.getDate() + trialDays);
      chargeDate = { minute, text: date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) };
    }
    return chargeDate.text;
  }

  function cleanText(node) {
    return (node?.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function consentText() {
    const block = document.querySelector('[data-jj-consent]');
    const label = cleanText(block?.querySelector('[data-jj-consent-label]'));
    const note = cleanText(block?.querySelector('[data-jj-consent-note]'));
    if (label) return [/[.!?]$/.test(label) ? label : `${label}.`, note].filter(Boolean).join(' ');
    return `Start my ${trialDays}-day free June Club trial. After the trial, ${cfg.priceLabel} every 30 days until I cancel. Cancel anytime at ${cfg.cancelHost}. Your first charge of ${cfg.priceLabel} is on ${firstChargeDate()}.`;
  }

  function setConsent(checked, { write = true } = {}) {
    if (checked) {
      state.consentAt = new Date().toISOString();
      state.consentToken = null; // taken from the cart the attribute is written to
    } else {
      state.consentAt = null;
      state.consentToken = null;
    }
    storeConsent();
    state.savedAt = null;
    state.savedTerms = null;
    if (checked && state.failed) showMessage(MESSAGES.membershipFailed);
    else if (checked) hideMessage();
    syncDom();
    emitChange();
    if (write) queueConsentWrite().catch((error) => warn('could not save the June Club choice', error));
  }

  /** Writes the current consent state to the cart attributes. Writes run one after another so the last one wins. */
  function queueConsentWrite() {
    state.writeChain = state.writeChain
      .catch(() => {})
      .then(async () => {
        const at = state.consentAt;
        syncDom(); // first charge date up to date before the text is read
        const attributes = at ? { [consentAttr]: at, [termsAttr]: consentText() } : { [consentAttr]: '', [termsAttr]: '' };
        const cart = await postJSON(routes.update, { attributes });
        const info = analyse(cart);
        adoptServerCart(cart, info);
        syncDom();
        if (info.hasVault && !info.hasMembership && !state.failed) reconcile();
      });
    return state.writeChain;
  }

  /* -------------------------------------------------------- DOM sync */

  function consentBlocks() {
    return [...document.querySelectorAll('[data-jj-consent]')];
  }

  function needsConsent(blocks = consentBlocks()) {
    return state.hasVault || state.hasMembership || blocks.length > 0;
  }

  function membershipMissing(blocks = consentBlocks()) {
    if (state.hasVault && !state.hasMembership) return true;
    return blocks.some((block) => block.getAttribute('data-jj-has-membership') === 'false');
  }

  function checkoutLocked(blocks = consentBlocks()) {
    return needsConsent(blocks) && (!state.consentAt || membershipMissing(blocks));
  }

  function lockElement(el, locked) {
    const isControl = el instanceof HTMLButtonElement || el instanceof HTMLInputElement;
    if (locked) {
      if (!el.hasAttribute('data-jj-locked')) el.setAttribute('data-jj-locked', '');
      if (el.getAttribute('aria-disabled') !== 'true') el.setAttribute('aria-disabled', 'true');
      if (isControl && !el.disabled) el.disabled = true;
    } else if (el.hasAttribute('data-jj-locked')) {
      // Only undo what this script set; a disabled state rendered by the theme (empty cart) stays.
      el.removeAttribute('data-jj-locked');
      el.removeAttribute('aria-disabled');
      if (isControl) el.disabled = false;
    }
  }

  function syncDom() {
    const blocks = consentBlocks();
    const ticked = Boolean(state.consentAt);
    const missing = membershipMissing(blocks);

    if (blocks.length) {
      const date = firstChargeDate();
      for (const block of blocks) {
        const input = block.querySelector('.jj-consent__input');
        if (input instanceof HTMLInputElement && input.checked !== ticked) input.checked = ticked;
        for (const span of block.querySelectorAll('[data-jj-first-charge]')) {
          if (span.textContent !== date) span.textContent = date;
        }
        if (ticked && !missing && !state.failed) {
          const message = block.querySelector('[data-jj-consent-message]');
          if (message instanceof HTMLElement && !message.hidden) message.hidden = true;
        }
      }
    }

    const locked = needsConsent(blocks) && (!ticked || missing);
    for (const el of document.querySelectorAll(CHECKOUT_SELECTOR)) {
      if (el instanceof HTMLElement && !el.closest('template')) lockElement(el, locked);
    }
  }

  /*
   * Re-lock after a morph. Only the cart-items-component roots are observed (the drawer and the cart
   * page hold every consent block and checkout button), so the vault countdown, sliders and Reign's
   * own DOM work elsewhere on the page never run syncDom. Mutations are batched to one syncDom per
   * frame; the setTimeout covers background tabs, where requestAnimationFrame does not fire. A click
   * in between is safe: the guards call syncDom themselves, and the CSS lock and the classic guard
   * read the live checkbox.
   */
  let syncQueued = false;
  function flushSync() {
    if (!syncQueued) return;
    syncQueued = false;
    syncDom();
  }
  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(flushSync);
    window.setTimeout(flushSync, 100);
  }

  const rootObserver = new MutationObserver(queueSync);
  const observedRoots = new WeakSet();
  function observeRoots() {
    for (const root of document.querySelectorAll('cart-items-component')) {
      if (observedRoots.has(root)) continue;
      observedRoots.add(root);
      rootObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'data-jj-has-vault', 'data-jj-has-membership'],
      });
    }
  }

  function showMessage(text, scope) {
    const blocks = scope ? [scope] : consentBlocks();
    for (const block of blocks) {
      const message = block.querySelector('[data-jj-consent-message]');
      if (!(message instanceof HTMLElement)) continue;
      if (message.textContent !== text) message.textContent = text;
      message.hidden = false;
    }
  }

  function hideMessage() {
    for (const block of consentBlocks()) {
      const message = block.querySelector('[data-jj-consent-message]');
      if (message instanceof HTMLElement) message.hidden = true;
    }
  }

  function blockFor(scope) {
    return (
      (scope instanceof Element && (scope.closest('[data-jj-consent]') || scope.querySelector('[data-jj-consent]'))) ||
      consentBlocks()[0] ||
      null
    );
  }

  function nudge(scope) {
    const block = blockFor(scope);
    if (!block) return;
    showMessage(state.consentAt ? MESSAGES.membership : MESSAGES.tick, block);
    block.classList.remove('jj-consent--attention');
    void block.offsetWidth; // restart the animation
    block.classList.add('jj-consent--attention');
    const input = block.querySelector('.jj-consent__input');
    if (input instanceof HTMLElement && !state.consentAt) input.focus();
  }

  function emitChange() {
    document.dispatchEvent(
      new CustomEvent('jj:vip:change', {
        detail: {
          hasVault: state.hasVault,
          hasMembership: state.hasMembership,
          consented: Boolean(state.consentAt),
          locked: checkoutLocked(),
        },
      })
    );
  }

  /* ---------------------------------------------------- checkout guard */

  function stop(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function proceed(trigger) {
    state.bypass = true;
    try {
      if (trigger instanceof HTMLButtonElement || trigger instanceof HTMLInputElement) {
        // With Reign installed the button has no form any more (its script strips `form`/`type`),
        // so this is a plain click that Reign's own handler turns into the redirect.
        const form = trigger.form;
        if (form && trigger.type === 'submit' && typeof form.requestSubmit === 'function') form.requestSubmit(trigger);
        else trigger.click();
      } else if (trigger instanceof HTMLFormElement) {
        if (typeof trigger.requestSubmit === 'function') trigger.requestSubmit();
        else trigger.submit();
      } else if (trigger instanceof HTMLElement) {
        trigger.click();
      }
    } finally {
      state.bypass = false;
    }
  }

  /** After a re-render the clicked node can be gone: the checkout button in the same cart section. */
  function liveTrigger(trigger, sectionId) {
    if (trigger.isConnected) return trigger;
    const scope = sectionId
      ? document.querySelector(`cart-items-component[data-section-id="${CSS.escape(sectionId)}"]`)
      : document;
    return scope?.querySelector('.cart__checkout-button') || document.querySelector('.cart__checkout-button');
  }

  /** True when a checkout click may go through untouched: consent saved on the cart and the server read is fresh. */
  function canPassStraight() {
    if (state.running || state.lineWrites || state.proceeding) return false;
    if (Date.now() - state.serverAt > FRESH_MS) return false;
    if (!state.consentAt || state.savedAt !== state.consentAt || membershipMissing()) return false;
    return state.savedTerms === consentText();
  }

  /**
   * Held checkout: read the server cart, bring the page and the consent attributes in line with it,
   * then click the same button again. Fails closed: on any error nothing goes to checkout.
   */
  async function verifyAndProceed(trigger) {
    if (state.proceeding) return;
    state.proceeding = true;
    const sectionId = trigger.closest('cart-items-component')?.getAttribute('data-section-id') || '';
    const scope = trigger.closest('.cart__ctas');
    trigger.setAttribute('aria-busy', 'true');
    let target = trigger;
    try {
      if (state.running) await state.running.catch(() => {});
      await waitForHorizon();
      const cart = await getCart();
      const info = analyse(cart);
      adoptServerCart(cart, info);
      syncDom();
      // The page must show the cart the server holds (and Reign charges) before anything goes out.
      await resyncPage(cart, info, { force: true });
      target = liveTrigger(trigger, sectionId);
      const ctas = target?.closest('.cart__ctas') || scope;

      if (!(info.hasVault || info.hasMembership)) {
        if (!checkoutLocked() && target) proceed(target);
        else nudge(ctas);
        return;
      }
      if (membershipMissing()) {
        // E.g. a page loaded without a cart check (Liquid saw no vault tag) holding a VAULT- SKU:
        // add the membership now, the consent block follows with the re-render.
        reconcile();
        if (!state.consentAt) nudge(ctas);
        else showMessage(state.failed ? MESSAGES.membershipFailed : MESSAGES.membership);
        return;
      }
      if (!state.consentAt) {
        nudge(ctas);
        return;
      }
      const terms = consentText();
      const attributes = cart.attributes || {};
      if (String(attributes[consentAttr] ?? '') !== state.consentAt || String(attributes[termsAttr] ?? '') !== terms) {
        await queueConsentWrite();
        if (state.savedAt !== state.consentAt || state.savedTerms !== terms) throw new Error('consent attribute not saved');
      }
      if (!target || checkoutLocked()) {
        nudge(ctas);
        return;
      }
      target.removeAttribute('aria-busy');
      proceed(target);
    } catch (error) {
      warn('checkout held', error);
      showMessage(MESSAGES.saveFailed, blockFor(scope) || undefined);
    } finally {
      state.proceeding = false;
      trigger.removeAttribute('aria-busy');
      target?.removeAttribute?.('aria-busy');
    }
  }

  function guard(event, trigger) {
    syncDom();
    if (needsConsent()) {
      if (!state.consentAt) {
        stop(event);
        nudge(trigger instanceof Element ? trigger.closest('.cart__ctas') : null);
        return;
      }
      if (membershipMissing()) {
        stop(event);
        showMessage(state.failed ? MESSAGES.membershipFailed : MESSAGES.membership);
        reconcile();
        return;
      }
    }
    if (canPassStraight()) return; // consent stored on a cart read moments ago: let Reign take over

    stop(event);
    if (trigger instanceof HTMLElement) verifyAndProceed(trigger);
  }

  function onClickCapture(event) {
    if (state.bypass) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest(CHECKOUT_SELECTOR);
    if (!trigger) {
      // A tap on the greyed-out checkout button lands on .cart__ctas (pointer-events: none on the button).
      const ctas = target.closest('.cart__ctas');
      if (ctas && !target.closest('[data-jj-consent]') && checkoutLocked()) nudge(ctas);
      return;
    }
    guard(event, trigger);
  }

  function onSubmitCapture(event) {
    if (state.bypass) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    const isCheckout =
      (submitter instanceof HTMLElement && (submitter.getAttribute('name') === 'checkout' || submitter.matches('.cart__checkout-button'))) ||
      /\/checkout/.test(form.getAttribute('action') || '') ||
      Boolean(form.querySelector('input[name="checkout"]'));
    if (!isCheckout) return;
    guard(event, submitter instanceof HTMLElement ? submitter : form);
  }

  function onChange(event) {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-jj-consent] .jj-consent__input')) {
      setConsent(input.checked);
    }
  }

  /** Fired by the classic guard in jj-vip-config.liquid when it stopped a click on a locked checkout. */
  function onBlocked(event) {
    const ctas = event.target instanceof Element ? event.target : null;
    if (!state.consentAt) nudge(ctas);
    else {
      showMessage(state.failed ? MESSAGES.membershipFailed : MESSAGES.membership, blockFor(ctas) || undefined);
      reconcile();
    }
  }

  /* ------------------------------------------------------------ wiring */

  window.addEventListener('click', onClickCapture, true);
  window.addEventListener('submit', onSubmitCapture, true);
  document.addEventListener('change', onChange);
  document.addEventListener('jj:vip:blocked', onBlocked);

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (state.own) return;
    const pending = /** @type {any} */ (event).promise;
    if (pending && typeof pending.then === 'function') {
      pending.then(
        () => reconcile(),
        () => reconcile()
      );
    } else {
      window.setTimeout(reconcile, 400);
    }
  });

  // F7: every cart change, ours included. cart-icon paints from the same promise first; one macrotask
  // later the shopper's own count goes over it. Without line items (Shopify.actions.updateCart) the
  // last known trial state is used, and the next server read (adoptServerCart) corrects it.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const pending = /** @type {any} */ (event).promise;
    if (!pending || typeof pending.then !== 'function') return;
    pending.then(
      async (result) => {
        await wait(0);
        const items = result?.detail?.items;
        let count = shopperCount(items);
        if (count == null && !Array.isArray(items) && state.hasMembership) {
          const total = Number(result?.cart?.totalQuantity ?? result?.detail?.itemCount);
          if (total > 1) count = total - 1;
        }
        paintCartCount(count);
      },
      () => {}
    );
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      const stored = readStore(STORE_CONSENT);
      state.consentAt = typeof stored?.at === 'string' ? stored.at : null;
      state.consentToken = typeof stored?.token === 'string' ? stored.token : null;
      state.serverAt = 0;
      reconcile();
    }
  });

  // Another tab may have changed the cart while this one was in the background. A tab whose cart is
  // empty skips the read: it has no checkout to protect, and its next cart change renders the server cart.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    state.serverAt = 0;
    observeRoots();
    if (state.itemCount > 0 || state.consentAt || state.hasVault || state.hasMembership || consentBlocks().length) reconcile();
  });

  // Theme editor: a re-rendered section can bring a new cart-items-component.
  document.addEventListener('shopify:section:load', () => {
    observeRoots();
    syncDom();
  });

  observeRoots();

  window.JJVip = Object.freeze({
    config: cfg,
    refresh: () => reconcile(),
    get state() {
      return {
        hasVault: state.hasVault,
        hasMembership: state.hasMembership,
        consentAt: state.consentAt,
        consentSaved: Boolean(state.consentAt) && state.savedAt === state.consentAt,
        locked: checkoutLocked(),
      };
    },
  });

  if (cfg.cart?.hasMembership && Number(cfg.cart?.itemCount) > 1) paintCartCount(Number(cfg.cart.itemCount) - 1);

  loadEvents();
  syncDom();
  emitChange();
  // Liquid already read the cart for this page view: without a vault piece, a membership line, a
  // saved consent attribute or a tick in this tab there is nothing to check (#11). A checkout click
  // still always reads the server cart first (see guard), so a VAULT- SKU without the tag, or a cart
  // changed in another tab, cannot slip through.
  if (cfg.cart?.hasVault || cfg.cart?.hasMembership || cfg.cart?.hasConsentAttribute || state.consentAt) reconcile();
}
